import React from "react";
import MuiTextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import type { TextFieldProps as MuiTextFieldProps } from "@mui/material/TextField";
import {
  styled,
  useTheme,
  SxProps,
  Theme,
  hexToRgb,
} from "@mui/material/styles";

export interface TextFieldProps extends Omit<MuiTextFieldProps, "variant"> {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  label?: string;
  size?: "small" | "medium";
  variant?: "outlined" | "filled" | "standard";
  disabled?: boolean;
  required?: boolean;
  value?: unknown;
  sx?: SxProps<Theme>;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
}

const StyledTextField = styled(MuiTextField)(
  ({ theme, color = "primary" }) => ({
    // width: "100%",
    // maxWidth: 369,

    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    "& .css-123qop1-MuiInputBase-root-MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(1),
    },

    "& .MuiInputBase-input": {
      fontFamily: "var(--font-inter)",
      height: "23px",
      fontWeight: 400,
      fontSize: "14px",
      // color: theme.palette.text.secondary,
      color: theme.palette.common.black,
      // back
    },

    "& .MuiInputBase-root": {
      // backgroundColor: theme.palette.background.paper,
      backgroundColor:"transparent",
      borderRadius: "10px",
      color: theme.palette.text.secondary,

      // "& .MuiInputBase-input": {
      //   padding: "9px 12px",
      // },
      "& .MuiOutlinedInput-notchedOutline": {
        borderWidth: "1px",
        borderColor: theme.palette.primary.main,
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderWidth: "2px",
        borderColor: theme.palette.primary.main,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: "2px",
        borderColor: theme.palette.primary.main,
      },
    },
  })
);

export const TextField: React.FC<TextFieldProps> = ({
  startIcon,
  endIcon,
  variant = "outlined",
  fullWidth = false,
  size = "small",
  error,
  onChange,
  helperText,
  sx,
  ...rest
}) => {
  const theme = useTheme();

  // Determine error state and message
  const hasError = typeof error === "string" ? true : Boolean(error);
  const errorMessage = typeof error === "string" ? error : helperText;

  return (
    <StyledTextField
      variant={variant}
      fullWidth={fullWidth}
      size={size}
      error={hasError}
      helperText={errorMessage}
      onChange={onChange}
      InputLabelProps={{
        shrink: true,
      }}
      InputProps={{
        notched: false, // This removes the notch in the border
        startAdornment: startIcon ? (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ) : undefined,
        endAdornment: endIcon ? (
          <InputAdornment position="end">{endIcon}</InputAdornment>
        ) : undefined,
        sx: {
          "& input::placeholder": {
            color: theme.palette.text.secondary,
            fontWeight: 400,
          },
        },
      }}
      sx={{ ...sx }}
      {...rest}
    />
  );
};

export default TextField;
